/**
 * The primary header — plan §3.4.1. 56px, identical on every page, and it never
 * carries page actions, tabs or breadcrumbs.
 *
 * Every measurement here is taken from
 * `/design/mockups/01-global-header/html/header-main.html`.
 */
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { ScopeSwitcher } from './scope-switcher.js'
import { Avatar } from '../components/primitives.js'
import { Toast } from '../components/overlays.js'
import { NotificationBell, NotificationsPanel, useNotifications } from './notifications.js'
import {
  IconCatalog, IconChevronDown, IconMenu,
  IconSearch, IconSettings, IconSparkles, IconUsersRoles,
} from './icons.js'
import { RowtedLogo } from './logo.js'
import { AccountMenu, type MenuRowSpec } from './account-menu.js'
import { useEnvironments } from './use-environments.js'
import { request } from '../lib/api.js'
import type { Theme } from '../lib/theme.js'

export interface HeaderProps {
  me: MeResponse
  unreadCount?: number
  theme: Theme
  onThemeChange: (theme: Theme) => void
  onOpenSearch: () => void
  onSignOut: () => void
  onScopeChange: (orgUnitIds: string[]) => void
  /**
   * Phone width, measured by the frame rather than guessed at from a media
   * query here — the frame is what decides whether there is a rail, and the
   * hamburger has to appear exactly when the rail goes away. One definition,
   * passed down.
   */
  phone?: boolean
  /** ☰ where there is no rail: a phone, or a page that asked for the nav sheet (Activity on an upright iPad). */
  menu?: boolean
  navOpen?: boolean
  onOpenNav?: () => void
}

export function Header({
  me, unreadCount, theme, onThemeChange, onOpenSearch, onSignOut, onScopeChange,
  phone = false, menu = phone, navOpen = false, onOpenNav,
}: HeaderProps) {
  /*
   * **The bell reads its own rows** (`docs/proposal.md` §12.3, migration
   * 0117). `unreadCount` was a prop with a default of nought that nothing
   * ever passed, beside a panel whose body was a sentence — which is the
   * right shape for a feature nothing raises yet and the wrong one the moment
   * something does. The prop stays, overriding the hook, because the Playwright
   * journey and the component tests set a badge without a server behind them.
   */
  const bell = useNotifications(true)
  const unread = unreadCount ?? bell.unread
  const navigate = useNavigate()
  const {
    environments, current, switchTo, error: switchError, dismissError,
  } = useEnvironments(request)
  const [open, setOpen] = useState<'scope' | 'notifications' | 'user' | null>(null)
  const toggle = (panel: 'scope' | 'notifications' | 'user') =>
    setOpen((current) => (current === panel ? null : panel))

  /*
   * Each menu is placed from its button rather than laid out inside it.
   *
   * `position: fixed` against a measured rectangle, which is what the list's
   * own settings menu already does, and for the same reason: a menu that is
   * `absolute` inside its trigger is at the mercy of every ancestor's
   * `overflow`, and there is always one. It also lets a 380px panel clamp
   * itself to the viewport instead of hanging off the right of a phone.
   *
   * The ref goes on the wrapper, not the button: the wrapper's box is the
   * button's box, and a `<button>` is not a forwardRef.
   */
  const bellAt = useRef<HTMLDivElement>(null)
  const userAt = useRef<HTMLDivElement>(null)


  /*
   * The account menu's Notifications row, and **only where there is no bell**
   * (Aaron: "notifications can be reached through the bell icon").
   *
   * On a desktop that is true and the row was a second door to a room with
   * one already, so it goes. On a phone the bell is not rendered at all — it
   * folds into this menu to give the search box its 36px back — so dropping
   * the row everywhere would leave a phone with no way to notifications. The
   * caller deciding is what keeps it to one rule: exactly one way to the bell
   * at every width.
   */
  const menuBell = phone
    ? { notifications: { count: unread, onOpen: () => setOpen('notifications') } }
    : {}

  return (
    /*
     * §3.4: "Collapses right-to-left as width shrinks; order never changes."
     * Nothing in here is allowed a fixed width — a 420px search box on a 390px
     * phone pushed the whole app sideways, and the app sliding under your
     * thumb is worse than a search box you have to tap.
     *
     * It does *not* clip. It used to, as a second line of defence against that
     * sideways push, and `overflow: hidden` on a 56px bar hides every menu
     * that hangs below it: the scope picker, the notifications and the account
     * menu all opened behind the page. Each child shrinks on its own now, and
     * the menus are placed from their buttons rather than inside them, so
     * neither the clip nor the thing it was guarding against is needed.
     */
    <>
    <header className={cn(
      'relative flex h-header shrink-0 items-center gap-2 border-b border-line',
      'bg-surface px-3 sm:gap-3 sm:px-4',
    )}>
      {/*
        * The hamburger, and only where there is no rail.
        *
        * §3.4 fixes the order of what is in this bar and this is not a
        * reordering of it: the rail moved into the header, so its control
        * stands where the rail did, at the left of everything.
        */}
      {menu && (
        <button
          type="button"
          onClick={onOpenNav}
          aria-label="Menu"
          aria-expanded={navOpen}
          className="-ml-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-field text-text hover:bg-hover"
        >
          <IconMenu size={20} />
        </button>
      )}

      <RowtedLogo tenantName={me.tenant.name === 'Rowted' ? null : me.tenant.name} />

      <span aria-hidden className="hidden h-[22px] w-px shrink-0 bg-line sm:block" />

      {/*
        * Where you are looking — `docs/scope-switcher.md`. It drives what
        * every page shows (plan §4 principle 4), so it sits beside the logo
        * and before the search.
        */}
      <ScopeSwitcher
        me={me}
        open={open === 'scope'}
        onToggle={() => toggle('scope')}
        onClose={() => setOpen(null)}
        onApply={(roots) => {
          onScopeChange(roots)
          setOpen(null)
        }}
      />

      {/* Universal search. ⌘K from anywhere (plan §6.1). */}
      <button
        type="button"
        onClick={onOpenSearch}
        aria-label="Search"
        className={cn(
          'flex h-9 min-w-0 flex-1 items-center gap-2 rounded-field border border-line bg-field',
          'px-2.5 text-text-muted hover:border-line-strong sm:ml-2 sm:px-3 lg:max-w-[420px]',
        )}
      >
        <span className="shrink-0"><IconSearch size={16} /></span>
        <span className="hidden flex-1 truncate text-left text-body sm:inline">
          Search jobs, customers, properties, invoices, people…
        </span>
        <kbd className="hidden rounded-tag border border-line px-1.5 py-px text-tag text-text-faint lg:inline">
          ⌘K
        </kbd>
      </button>

      <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1">
        {/*
          * The bell, on everything but a phone.
          *
          * On a phone it folds into the account menu, which is where the rest
          * of "things about you" already lives — and the 36px it gives back is
          * the difference between a search box you can read and one you
          * cannot. The count is not lost with it: the avatar carries a dot,
          * and the menu's first row carries the number.
          *
          * Not rendered rather than hidden: a `display: none` button is still
          * a button to anything walking the DOM, and the first thing to walk
          * into it was the journey that clicks every menu in this bar.
          */}
        {!phone && (
          <NotificationBell
            ref={bellAt}
            count={unread}
            open={open === 'notifications'}
            onToggle={() => toggle('notifications')}
          />
        )}

        <button
          type="button"
          aria-label="Rowted AI"
          className="hidden h-9 w-9 items-center justify-center rounded-field text-accent hover:bg-hover sm:inline-flex"
        >
          <IconSparkles size={19} />
        </button>

        <div ref={userAt} className="relative sm:ml-1">
          <button
            type="button"
            onClick={() => toggle('user')}
            aria-expanded={open === 'user'}
            className="inline-flex h-[38px] items-center gap-2 rounded-full py-0 pl-1 pr-1.5 hover:bg-hover"
          >
            <span className="relative flex">
              <Avatar name={me.user.name} self size={30} />
              {/* The bell's badge, when there is no bell. */}
              {phone && unread > 0 && (
                <i
                  aria-hidden
                  className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-[1.5px] border-surface bg-danger"
                />
              )}
            </span>
            {/* iPad and phone collapse this to the avatar alone (plan §3.4). */}
            <span className="hidden flex-col leading-[1.15] text-left lg:flex">
              <b className="text-[12.5px]">{me.user.name}</b>
              <span className="text-tag font-normal text-text-muted">{me.user.email}</span>
            </span>
            <IconChevronDown size={13} className="text-text-faint" />
          </button>
          <AccountMenu
            open={open === 'user'}
            onClose={() => setOpen(null)}
            anchor={userAt.current}
            who={{
              name: me.user.name,
              email: me.user.email,
              /* One line, board 1: role, then the ground they can see. The
                 label comes from `shared/src/scope.ts` like every other
                 place that names where you are looking. */
              line: [me.roles.map((role) => role.name).join(', '), me.scope.label]
                .filter(Boolean).join(' · '),
            }}
            theme={theme}
            onThemeChange={onThemeChange}
            profileHref="/account"
            onGo={(path) => { window.location.href = path }}
            onSignOut={onSignOut}
            {...menuBell}
            environments={environments}
            current={current}
            onSwitch={(target) => { void switchTo(target) }}
            rows={menuRows(me)}
          />
          {/*
            * One notifications panel, hung off whichever control opened it —
            * the bell on a desktop, the avatar on a phone, where the bell is
            * a row in the menu instead.
            */}
          <NotificationsPanel
            open={open === 'notifications'}
            onClose={() => setOpen(null)}
            anchor={phone ? userAt.current : bellAt.current}
            items={bell.items}
            onRead={bell.markRead}
            onOpenLink={navigate}
          />
        </div>
      </div>
    </header>

    {/* A switch that did not happen has to say so: the menu has already
        closed by the time the server answers, so nothing else would. */}
    {switchError && (
      <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
        <Toast tone="danger" onDismiss={dismissError}>{switchError}</Toast>
      </div>
    )}
    </>
  )
}

/**
 * The rows the office app contributes to the account menu.
 *
 * Plan §3.4: a row the role cannot use is removed, not greyed — which is why
 * this is a function of `me` rather than a constant with `disabled` flags.
 */
function menuRows(me: MeResponse): MenuRowSpec[] {
  const go = (path: string) => () => { window.location.href = path }
  const rows: MenuRowSpec[] = []
  if (me.permissions.employees !== 'none') {
    rows.push({
      label: 'People',
      icon: IconUsersRoles,
      sub: 'who is here and what they can reach',
      onClick: go('/settings/people'),
    })
  }
  /* Anybody who can quote can see what they are quoting from; changing it is
     gated on the server, not here. */
  if (me.permissions.quotes !== 'none') {
    rows.push({
      label: 'Catalog',
      icon: IconCatalog,
      sub: 'what a quote is built out of',
      onClick: go('/settings/catalog'),
    })
  }
  if (me.permissions.company_settings !== 'none') {
    rows.push({
      label: 'Settings',
      icon: IconSettings,
      tag: 'admin',
      sub: 'company, features, money, scheduling',
      onClick: go('/settings'),
    })
  }
  return rows
}
